import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Coffee, Play, Pause, Utensils, Target } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const FOCUS_MIN = 50, PAUSE_MIN = 10, LUNCH_MIN = 60, CYCLES_BEFORE_LUNCH = 3;

/**
 * Floating widget bottom-right: shows the current cycle state, remaining
 * time, and lets the manager start/end pauses & lunch. Fires a browser
 * notification when a cycle ends so the manager gets nudged even if
 * they're in another tab (Whatsapp/CRM).
 */
export default function ShiftTimer({ shift, onUpdate }) {
  const [now, setNow] = useState(Date.now());
  // На телефоне панель 280px перекрывала таблицы (заявки ателье было не
  // открыть) — на узких экранах стартуем свёрнутыми.
  const [minimised, setMinimised] = useState(
    typeof window !== "undefined" && window.innerWidth < 640);
  const notifiedFor = useRef(null);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // Ask permission once so we can send desktop notifications.
  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      try { Notification.requestPermission(); } catch { /* noop */ }
    }
  }, []);

  const state = shift.cycle?.state || "focus";
  const startedAt = shift.cycle?.cycle_started_at
    ? new Date(shift.cycle.cycle_started_at).getTime()
    : Date.now();
  const cyclesDone = shift.cycle?.cycles_completed || 0;
  const targetMin = state === "focus" ? FOCUS_MIN
                    : state === "pause" ? PAUSE_MIN
                    : state === "lunch" ? LUNCH_MIN : FOCUS_MIN;
  const elapsedSec = Math.max(0, Math.floor((now - startedAt) / 1000));
  const remainingSec = Math.max(0, targetMin * 60 - elapsedSec);
  const mm = String(Math.floor(remainingSec / 60)).padStart(2, "0");
  const ss = String(remainingSec % 60).padStart(2, "0");
  const overtime = elapsedSec > targetMin * 60;

  // Browser notification when the cycle just crossed the boundary.
  useEffect(() => {
    if (overtime && notifiedFor.current !== shift.cycle?.cycle_started_at) {
      notifiedFor.current = shift.cycle?.cycle_started_at;
      try {
        if ("Notification" in window && Notification.permission === "granted") {
          const title = state === "focus"
            ? "Пора перезагрузиться — 10 минут"
            : "Продолжаем — новый фокус";
          new Notification(title, {
            body: state === "focus"
              ? "Отойди от рабочего места: пройдись, разомнись, выпей воды."
              : "Вернись в чат — впереди 50 минут концентрации.",
            silent: false,
          });
        }
      } catch { /* noop */ }
    }
  }, [overtime, state, shift.cycle?.cycle_started_at]);

  const patch = async (payload) => {
    try {
      await axios.patch(`${API}/admin/shift/current`, payload,
                          { withCredentials: true });
      onUpdate && onUpdate();
    } catch { /* noop */ }
  };

  const startPause = () => patch({ start_break: "pause" });
  const startLunch = () => patch({ start_break: "lunch" });
  const endBreak = () => patch({ end_break: true });

  const shouldSuggestLunch = state === "focus" && cyclesDone >= CYCLES_BEFORE_LUNCH
    && !(shift.breaks || []).some(b => b.kind === "lunch");

  if (minimised) {
    return (
      <button data-testid="shift-timer-mini" onClick={() => setMinimised(false)}
        style={{
          position: "fixed", bottom: 20, right: 20, zIndex: 90,
          width: 54, height: 54, borderRadius: "50%", border: "none",
          background: "linear-gradient(135deg,#C5A059,#D4B878)", color: "#0A0A0A",
          fontFamily: "Manrope", fontWeight: 700, fontSize: 12, cursor: "pointer",
          boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
        }}>
        {mm}:{ss}
      </button>
    );
  }

  return (
    <div data-testid="shift-timer-widget"
      style={{
        position: "fixed", bottom: 20, right: 20, zIndex: 90,
        background: "white", borderRadius: 12, padding: 16,
        border: "1px solid #e5e7eb", width: 280,
        boxShadow: "0 12px 32px rgba(0,0,0,0.15)",
        fontFamily: "Manrope",
      }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.2,
                       textTransform: "uppercase", color: "#8a8a89" }}>
          {state === "focus" && "🎯 Фокус"}
          {state === "pause" && "☕ Перерыв"}
          {state === "lunch" && "🍽 Обед"}
        </div>
        <button data-testid="shift-timer-minimise" onClick={() => setMinimised(true)}
          style={{ background: "none", border: "none", cursor: "pointer",
                    color: "#94a3b8", fontSize: 18, lineHeight: 1 }}>—</button>
      </div>
      <div style={{ fontFamily: "Cormorant Garamond", fontSize: 42, fontWeight: 600,
                     color: overtime ? "#DC2626" : "#0A0A0A", lineHeight: 1 }}>
        {mm}:{ss}
      </div>
      <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>
        Циклов сегодня: <b>{cyclesDone}</b>
        {overtime && " · время вышло"}
      </div>

      {/* Guidance line */}
      <div style={{ marginTop: 10, padding: "8px 10px", background: "#faf9f5",
                     borderRadius: 6, fontSize: 11.5, color: "#374151", lineHeight: 1.4 }}>
        {state === "focus" && "Отвечайте клиентам, двигайте диалоги, фиксируйте результат в CRM."}
        {state === "pause" && "Отойдите от экрана: разминка, вода, взгляд в окно."}
        {state === "lunch" && "Полный переключатор мозга — вернитесь с новой энергией."}
      </div>

      {/* Actions */}
      <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
        {state === "focus" && (
          <>
            <button data-testid="shift-btn-pause" onClick={startPause}
              style={btn("#f5f5f4", "#374151")}>
              <Coffee size={13} /> Перерыв
            </button>
            {shouldSuggestLunch && (
              <button data-testid="shift-btn-lunch" onClick={startLunch}
                style={btn("linear-gradient(135deg,#C5A059,#D4B878)", "#0A0A0A")}>
                <Utensils size={13} /> Обед
              </button>
            )}
          </>
        )}
        {(state === "pause" || state === "lunch") && (
          <button data-testid="shift-btn-resume" onClick={endBreak}
            style={btn("linear-gradient(135deg,#C5A059,#D4B878)", "#0A0A0A")}>
            <Play size={13} /> Вернуться к работе
          </button>
        )}
      </div>

      {/* Live stats */}
      {shift.auto_stats && (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid #f1f1ee",
                       display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
          <StatMini label="Лиды" value={shift.auto_stats.leads_processed || 0} />
          <StatMini label="Продажи" value={shift.auto_stats.sales_count || 0} />
          <StatMini label="Выручка"
            value={((shift.auto_stats.revenue || 0) / 1000).toFixed(0) + "к"} />
          <StatMini label="План"
            value={
              shift.goal?.revenue > 0
                ? Math.round(((shift.auto_stats.revenue || 0) / shift.goal.revenue) * 100) + "%"
                : "—"
            } tone="#B45309" />
        </div>
      )}
    </div>
  );
}

const btn = (bg, color) => ({
  flex: 1, background: bg, color, border: "none", borderRadius: 6,
  padding: "8px 10px", fontFamily: "Manrope", fontSize: 11, fontWeight: 700,
  cursor: "pointer", display: "inline-flex", alignItems: "center",
  justifyContent: "center", gap: 5,
});
function StatMini({ label, value, tone }) {
  return (
    <div style={{ background: "#faf9f5", borderRadius: 6, padding: "6px 8px" }}>
      <div style={{ fontSize: 9, color: "#8a8a89", textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontFamily: "Cormorant Garamond", fontSize: 18, fontWeight: 700, color: tone || "#0A0A0A" }}>{value}</div>
    </div>
  );
}
